import { initPasswordToggles } from '@/js/autorization/passwordTogler';
import { createCooldownWithLock } from '@/js/autorization/timer.utils';

(() => {
  const P = {
    COOLDOWN_SEC: 60,
    RESEND_LOCK_KEY: 'recover_sms_resend_locked',

    init() {
      this.form = document.querySelector('#passRecoverForm');
      if (!this.form) return;

      if (!this.urlParam('id')) {
        window.location.href = RecoverConf.urlLogin;
        return;
      }

      initPasswordToggles();

      this.code = document.querySelector('#smsCode');
      this.codeErr = document.querySelector('#smsCodeErr');

      this.pass = document.querySelector('#pass');
      this.passErr = document.querySelector('#passErr');

      this.passConf = document.querySelector('#passConf');
      this.passConfErr = document.querySelector('#passConfErr');

      // resend UI
      this.timerEl = document.querySelector('#confirmSmsTimer');
      this.resendBtn = document.querySelector('#resendSmsBtn');
      this.resendErr = document.querySelector('#smsError') || this.codeErr;

      this.hide(this.codeErr);
      this.hide(this.passConfErr);

      this.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.setPassState();

      const confirmSmsPhone = document.querySelector('#confirmSmsPhone');
      if (confirmSmsPhone) {
        confirmSmsPhone.textContent = JSON.parse(sessionStorage.getItem('recover_phone_data'))?.phone || '';
      }

      this.initResendTimer();
    },

    /* ================= resend timer (shared util) ================= */

    isResendLocked() {
      return sessionStorage.getItem(this.RESEND_LOCK_KEY) === '1';
    },
    lockResend() {
      sessionStorage.setItem(this.RESEND_LOCK_KEY, '1');
    },
    unlockResend() {
      sessionStorage.removeItem(this.RESEND_LOCK_KEY);
    },

    initResendTimer() {
      if (!this.timerEl && !this.resendBtn) return;

      this.cooldown = createCooldownWithLock({
        seconds: this.COOLDOWN_SEC,
        lockedFn: () => this.isResendLocked(),
        onTick: (secLeft) => {
          if (!this.timerEl) return;
          this.timerEl.textContent = `${Math.max(0, secLeft | 0)} сек`;
        },
        onEnable: () => {
          if (this.resendBtn) this.resendBtn.disabled = false;
        },
      });

      // new flow -> allow ONE resend again
      this.unlockResend();

      this.startCooldown(this.COOLDOWN_SEC);

      if (this.resendBtn) {
        this.resendBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.onResendClick();
        });

        this.resendBtn.disabled = this.isResendLocked() ? true : this.resendBtn.disabled;
      }
    },

    startCooldown(seconds = this.COOLDOWN_SEC) {
      if (this.resendBtn) this.resendBtn.disabled = true;
      this.cooldown?.start?.(seconds);
    },

    /* ================= reCAPTCHA v3 for resend ================= */

    async getRecaptchaV3Token(action) {
      const siteKey = RecoverConf?.recaptcha_key_v3; // ✅ expects key in config
      if (!siteKey) return '';

      if (typeof grecaptcha === 'undefined' || !grecaptcha?.ready || !grecaptcha?.execute) return '';

      return await new Promise((resolve) => {
        try {
          grecaptcha.ready(() => {
            grecaptcha
              .execute(siteKey, { action })
              .then((token) => resolve((token || '').toString()))
              .catch(() => resolve(''));
          });
        } catch (e) {
          resolve('');
        }
      });
    },

    async onResendClick() {
      if (!this.resendBtn) return;

      if (this.isResendLocked()) return;
      if (this.cooldown?.getSecondsLeft?.() > 0) return;

      this.resendBtn.disabled = true;

      try {
        const action = 'customer_password_restore';

        const token = await this.getRecaptchaV3Token(action);
        if (!token) {
          this.setTextAndShow(this.resendErr, RecoverConf.msg.tryAgain);
          this.resendBtn.disabled = false;
          return;
        }

        const phone = JSON.parse(sessionStorage.getItem('recover_phone_data'))?.phone || '';
        const id = this.urlParam('id');

        const url = RecoverConf.urlResendSms || RecoverConf.urlResend;
        if (!url) throw new Error('No resend url');

        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            phone,
            id,
            rec_token: token,
            rec_ver: 'v3',
            rec_action: action,
          }),
        });

        const data = await res.json().catch(() => null);

        if (res.ok && data?.response) {
          // after successful resend -> lock forever and keep disabled after time
          this.lockResend();
          this.startCooldown(this.COOLDOWN_SEC);
          return;
        }

        const msg = data?.message || RecoverConf.msg.tryAgain;
        this.setTextAndShow(this.resendErr, msg);
        this.resendBtn.disabled = false;
      } catch (e) {
        this.setTextAndShow(this.resendErr, RecoverConf.msg.tryAgain);
        this.resendBtn.disabled = false;
      }
    },

    /* ================= existing logic ================= */

    onFormSubmit(e) {
      e.preventDefault();

      if (!this.validate()) return false;
      this.setFormDisabled(true);

      const reqData = {
        code: this.value(this.code),
        id: this.urlParam('id'),
      };

      if (this.isVisible(this.pass)) reqData.password = this.value(this.pass);
      if (this.isVisible(this.passConf)) reqData.password_confirmation = this.value(this.passConf);

      fetch(RecoverConf.urlCheckCode, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Accept': 'application/json',
        },
        body: JSON.stringify(reqData),
      })
        .then(async (res) => {
          let data = null;
          try { data = await res.json(); } catch (err) {}

          if (!res.ok || !data) throw new Error('Bad response');

          if (data.response) {
            sessionStorage.setItem('recovery-confirm', '1');
            if (data.data && data.data.activation_url) {
              window.location.href = data.data.activation_url;
            } else {
              window.location.href = RecoverConf.urlLogin;
            }
          } else {
            const msg = data.message ? data.message : RecoverConf.msg.tryAgain;
            this.setTextAndShow(this.codeErr, msg);

            window.GTM?.unsuccessfulPasswordRecoveryEvent?.(['server', data.code]);

            this.setFormDisabled(false);
          }
        })
        .catch(() => {
          window.GTM?.unsuccessfulPasswordRecoveryEvent?.(['server']);
          this.setTextAndShow(this.codeErr, RecoverConf.msg.tryAgain);
          this.setFormDisabled(false);
        });

      return false;
    },

    validate() {
      let isValid = true;

      this.hide(this.codeErr);
      if (!this.value(this.code)) {
        isValid = false;
        this.setTextAndShow(this.codeErr, RecoverConf.msg.required);
        window.GTM?.unsuccessfulPasswordRecoveryEvent?.(['code']);
      }

      this.hide(this.passErr);
      this.hide(this.passConfErr);

      if (this.isVisible(this.pass)) {
        const passVal = this.value(this.pass);
        const confVal = this.value(this.passConf);

        if (!passVal) {
          isValid = false;
          this.setTextAndShow(this.passErr, RecoverConf.msg.required);
          window.GTM?.unsuccessfulPasswordRecoveryEvent?.(['password']);
        } else if (passVal.length < 8) {
          isValid = false;
          this.setTextAndShow(this.passErr, RecoverConf.msg.minPassLength);
          window.GTM?.unsuccessfulPasswordRecoveryEvent?.(['password']);
        }

        if (!confVal) {
          isValid = false;
          this.setTextAndShow(this.passConfErr, RecoverConf.msg.required);
          window.GTM?.unsuccessfulPasswordRecoveryEvent?.(['password-confirm']);
        }

        if (passVal && confVal && passVal !== confVal) {
          isValid = false;
          this.setTextAndShow(this.passConfErr, RecoverConf.msg.passNotEqual);
          window.GTM?.unsuccessfulPasswordRecoveryEvent?.(['password-confirm', 'password', 'not-equal']);
        }
      }

      return isValid;
    },

    setPassState() {
      document.querySelectorAll('.input--pass').forEach((el) => this.show(el));
    },

    urlParam(name) {
      const params = new URLSearchParams(window.location.search);
      const v = params.get(name);
      return v ? v : false;
    },

    setFormDisabled(disabled) {
      this.form.querySelectorAll('input, select, textarea, button').forEach((el) => {
        el.disabled = !!disabled;
      });
    },
    value(el) {
      return (el?.value ?? '').toString();
    },
    isVisible(el) {
      if (!el) return false;
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    },
    hide(el) {
      if (!el) return;
      el.style.display = 'none';
      const inputEl = el.previousElementSibling?.querySelector?.('input') || el.previousElementSibling;
      if (inputEl && inputEl.tagName === 'INPUT') {
        inputEl.setAttribute('aria-invalid', 'false');
      }
    },
    show(el) {
      if (!el) return;
      el.style.display = '';
      const inputEl = el.previousElementSibling?.querySelector?.('input') || el.previousElementSibling;
      if (inputEl && inputEl.tagName === 'INPUT') {
        inputEl.setAttribute('aria-invalid', 'true');
        inputEl.focus();
      }
    },
    setTextAndShow(el, text) {
      if (!el) return;
      el.textContent = text ?? '';
      this.show(el);
    },
  };

  document.addEventListener('DOMContentLoaded', () => P.init());
})();
